/**
 * ST-3a — the tag's own page. What's wrong, what is attached, where it lives,
 * and the two steps it takes to close: whoever did the work marks it done, and
 * the person who placed it gives the final OK.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import EntityThread from '../components/EntityThread';
import { TellSomeoneSheet } from '../components/TellSomeoneSheet';
import AskDesignerSheet, { AskedLabel } from '../components/AskDesignerSheet';
import '../styles/site-tags.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const clock = (when) => (when
  ? new Date(when).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '');
const day = (when) => (when
  ? new Date(when).toLocaleDateString([], { month: 'short', day: 'numeric' }) : '');

const PILL = {
  open: ['kst-pill kst-pill--late', 'Open'],
  done: ['kst-pill kst-pill--ok', 'Done'],
  void: ['kst-pill', 'Void'],
  blank: ['kst-pill', 'Blank'],
};

export default function SiteTagPage() {
  const { tagId } = useParams();
  const [tag, setTag] = useState(null);
  const [note, setNote] = useState('');
  const [saved, setSaved] = useState('');
  const [problem, setProblem] = useState('');
  const [picker, setPicker] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [why, setWhy] = useState('');
  const [asking, setAsking] = useState('');
  const [telling, setTelling] = useState(false);
  // RS-2b — the question this tag raises.
  const [askingDesigner, setAskingDesigner] = useState(false);
  const [choices, setChoices] = useState([]);
  const [big, setBig] = useState(null);
  const [debounce, setDebounce] = useState(null);

  const load = useCallback(async () => {
    const { data } = await axios.get(`${API}/site-tags/${tagId}`);
    setTag(data);
    setNote(data.note || '');
  }, [tagId]);

  useEffect(() => { load().catch(() => setProblem('That tag could not be read')); }, [load]);

  useEffect(() => {
    const key = (e) => { if (e.key === 'Escape') setBig(null); };
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, []);

  const act = async (call) => {
    setProblem('');
    try {
      const { data } = await call();
      setTag(data);
      setSaved(`Saved ${clock(new Date())}`);
      setAsking('');
      setWhy('');
    } catch (e) {
      setProblem(e?.response?.data?.detail || 'That did not save');
    }
  };

  const patch = (body) => act(() => axios.patch(`${API}/site-tags/${tagId}`, body));

  const typeNote = (text) => {
    setNote(text);
    if (debounce) clearTimeout(debounce);
    setDebounce(setTimeout(() => patch({ note: text }), 600));
  };

  const sendPhoto = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    await axios.post(`${API}/site-tags/${tagId}/photos`, form).catch(() => {});
    await load();
  };

  const sendFile = async (event) => {
    const file = event.target.files && event.target.files[0];
    event.target.value = '';
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    setProblem('');
    try {
      await axios.post(`${API}/site-tags/${tagId}/attachments`, form);
      await load();
    } catch (e) {
      setProblem(e?.response?.data?.detail || 'That file was not attached');
    }
  };

  const addLink = async () => {
    setProblem('');
    try {
      await axios.post(`${API}/site-tags/${tagId}/links`, { url: linkUrl });
      setLinkUrl('');
      setAsking('');
      await load();
    } catch (e) {
      setProblem(e?.response?.data?.detail || 'That link was not attached');
    }
  };

  const removeAttachment = async (aid) => {
    setProblem('');
    try {
      await axios.delete(`${API}/site-tags/${tagId}/attachments/${aid}`);
      await load();
    } catch (e) {
      setProblem(e?.response?.data?.detail || 'That attachment stayed put');
    }
  };

  const openProjects = async () => {
    const { data } = await axios.get(`${API}/site-tags/project-choices`);
    setChoices(data.projects || []);
    setPicker('project');
  };

  if (!tag) {
    return (
      <div className="kst" data-testid="site-tag-page">
        <p className="kst-k">{problem || 'Reading the tag…'}</p>
      </div>
    );
  }

  // ST-5 — a tag placed from a phone has no KEY-MADE user behind it, so the
  // firm gives the OK and the pill says so.
  const fromAPhone = ['public', 'party'].includes(tag.placed_by_kind);
  const pill = tag.state === 'done_pending'
    ? ['kst-pill kst-pill--wait', fromAPhone
      ? "Done — waiting for the firm's OK"
      : `Done — waiting for ${tag.placed_by_name}'s OK`]
    : (PILL[tag.state] || PILL.blank);
  const closed = tag.state === 'done' || tag.state === 'void';

  return (
    <div className="kst" data-testid="site-tag-page">
      <div className="kst-hd">
        <div>
          <h1 className="kst-t">Tag {tag.tag_number} — {tag.title || 'no words yet'}</h1>
          <p className="kst-k kst-pills">
            <span className={pill[0]} data-testid="site-tag-state">{pill[1]}</span>
            {tag.punch_item_id ? (
              <span className="kst-pill" data-testid="site-tag-punch-pill">
                Punch item · {tag.project_name}
              </span>
            ) : null}
          </p>
          <p className="kst-k" data-testid="site-tag-meta">
            {`Placed by ${tag.placed_by_label || tag.placed_by_name || 'nobody yet'} · ${day(tag.placed_at)} · `
              + `${tag.room || 'no room'} · ${tag.trade || 'no trade'} · `
              + `scanned ${tag.scan_count} times`}
          </p>
        </div>
      </div>

      {problem ? <p className="kst-err" data-testid="site-tag-error">{problem}</p> : null}

      <div className="kstp">
        <div className="kstp-col">
          <section className="kstp-block">
            <p className="kst-k">What&apos;s wrong</p>
            <textarea className="kstp-area" rows={5} value={note}
              placeholder="In your words…" disabled={tag.state === 'void'}
              onChange={(e) => typeNote(e.target.value)}
              onBlur={() => patch({ note })} data-testid="site-tag-note" />
            <p className="kst-k" data-testid="site-tag-saved">{saved}</p>
            <div className="kstp-thumbs">
              {(tag.photos || []).map((photo, index) => (
                <button type="button" key={photo.url} className="kstp-thumb"
                  onClick={() => setBig(photo.url)} data-testid={`site-tag-thumb-${index}`}>
                  <img src={photo.url} alt={photo.filename || ''} />
                </button>
              ))}
              <label className="kstp-thumb kstp-thumb--add" data-testid="site-tag-photo">
                +
                <input type="file" accept="image/*" capture="environment" hidden
                  onChange={sendPhoto} />
              </label>
            </div>
          </section>

          <section className="kstp-block">
            <p className="kst-k">Attached</p>
            <div className="kstp-rows">
              {(tag.attachments || []).map((att) => (
                <div className="kstp-row" key={att.id}>
                  <span className="kst-pill">
                    {att.kind === 'link' ? 'Link'
                      : (att.filename || '').toLowerCase().endsWith('.pdf') ? 'PDF' : 'File'}
                  </span>
                  <a className="kstp-link" href={att.url} target="_blank" rel="noreferrer">
                    {att.title || att.filename || att.url}
                  </a>
                  {att.preview?.site && att.preview.site !== att.title ? (
                    <span className="kst-muted">{att.preview.site}</span>
                  ) : null}
                  <button type="button" className="kst-btn"
                    onClick={() => removeAttachment(att.id)}
                    data-testid={`site-tag-att-remove-${att.id}`}>Remove</button>
                </div>
              ))}
              {!(tag.attachments || []).length ? (
                <p className="kst-muted">Nothing attached yet.</p>
              ) : null}
            </div>
            <div className="kstp-acts">
              <label className="kst-btn" data-testid="site-tag-add-file">
                Add a file or PDF
                <input type="file" hidden onChange={sendFile} />
              </label>
              <button type="button" className="kst-btn"
                onClick={() => setAsking(asking === 'link' ? '' : 'link')}
                data-testid="site-tag-add-link">Add a web link</button>
            </div>
            {asking === 'link' ? (
              <div className="kstp-ask">
                <input className="kstp-in" placeholder="https://…" value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  data-testid="site-tag-link-url" />
                <button type="button" className="kst-btn kst-btn--brand" onClick={addLink}
                  data-testid="site-tag-link-save">Attach it</button>
              </div>
            ) : null}
          </section>
        </div>

        <div className="kstp-col">
          <section className="kstp-block">
            <p className="kst-k">Where it lives</p>
            <div className="kstp-rows">
              <div className="kstp-row">
                <span className="kst-muted">Project</span>
                <span>{tag.project_name || 'No project · firm tag'}</span>
                {closed ? null : (
                  <button type="button" className="kst-btn" onClick={openProjects}
                    data-testid="site-tag-change-project">Change project</button>
                )}
              </div>
              <div className="kstp-row">
                <span className="kst-muted">Trade</span>
                <span>{tag.trade || '—'}</span>
                {closed ? null : (
                  <button type="button" className="kst-btn" onClick={() => setPicker('trade')}
                    data-testid="site-tag-trade">Pick</button>
                )}
              </div>
              <div className="kstp-row">
                <span className="kst-muted">Room</span>
                <span>{tag.room || '—'}</span>
                {closed ? null : (
                  <button type="button" className="kst-btn" onClick={() => setPicker('room')}
                    data-testid="site-tag-room">Pick</button>
                )}
              </div>
            </div>

            {tag.rfi?.answer ? (
              <p className="kst-muted" data-testid="site-tag-rfi-answer">
                {`${tag.rfi.rfi_number} answered — ${tag.rfi.answer}`}
              </p>
            ) : null}

            <div className="kstp-acts">
              {/* ST-3b — a tag is often somebody else's to act on. */}
              <button type="button" className="kst-btn"
                onClick={() => setTelling(true)} data-testid="site-tag-tell">
                Tell someone
              </button>
              {/* RS-2b — or ask the designer, and the answer comes back here. */}
              {tag.rfi ? (
                <AskedLabel rfi={tag.rfi} projectId={tag.project_id}
                  testid="site-tag-rfi" />
              ) : (
                <button type="button" className="kst-btn"
                  disabled={!tag.project_id}
                  onClick={() => setAskingDesigner(true)} data-testid="site-tag-ask">
                  Ask the designer
                </button>
              )}
              {tag.state === 'open' ? (
                <button type="button" className="kst-btn kst-btn--brand"
                  onClick={() => act(() => axios.post(`${API}/site-tags/${tagId}/done`))}
                  data-testid="site-tag-done">Mark done</button>
              ) : null}
              {tag.state === 'done_pending' && tag.may_close ? (
                <>
                  <button type="button" className="kst-btn kst-btn--brand"
                    onClick={() => act(() => axios.post(`${API}/site-tags/${tagId}/agree`))}
                    data-testid="site-tag-agree">Agree — close it</button>
                  <button type="button" className="kst-btn"
                    onClick={() => setAsking(asking === 'back' ? '' : 'back')}
                    data-testid="site-tag-send-back">Send it back</button>
                </>
              ) : null}
              {!closed && tag.may_close ? (
                <button type="button" className="kst-btn"
                  onClick={() => setAsking(asking === 'void' ? '' : 'void')}
                  data-testid="site-tag-void">Void this tag</button>
              ) : null}
            </div>

            {asking === 'back' || asking === 'void' ? (
              <div className="kstp-ask">
                <input className="kstp-in" maxLength={300} value={why}
                  placeholder={asking === 'void' ? 'Why is this tag dead?'
                    : 'What is still wrong?'}
                  onChange={(e) => setWhy(e.target.value)} data-testid="site-tag-why" />
                <button type="button" className="kst-btn kst-btn--brand"
                  onClick={() => act(() => axios.post(
                    `${API}/site-tags/${tagId}/${asking === 'void' ? 'void' : 'send-back'}`,
                    { why }))}
                  data-testid="site-tag-why-confirm">
                  {asking === 'void' ? 'Void it' : 'Send it back'}
                </button>
              </div>
            ) : null}

            {tag.state === 'void' ? (
              <p className="kst-muted" data-testid="site-tag-void-reason">
                {`Voided by ${tag.voided_by_name} · ${day(tag.voided_at)} — ${tag.void_reason}`}
              </p>
            ) : null}
          </section>

          <section className="kstp-block" data-testid="site-tag-thread">
            <p className="kst-k">Conversation</p>
            <EntityThread entityType={tag.thread_entity.type} entityId={tag.thread_entity.id}
              party="internal" axiosInstance={axios} canPost={tag.state !== 'void'}
              emptyLabel="Nothing said yet." testIdPrefix="site-tag-thread" />
          </section>
        </div>
      </div>

      {picker ? (
        <div className="kstw-sheet" data-testid={`site-tag-picker-${picker}`}>
          <p className="kst-k">
            {picker === 'project' ? 'Which project?'
              : picker === 'room' ? 'Which room?' : 'Which trade?'}
          </p>
          <div className="kstw-list">
            {picker === 'project' ? (
              <>
                {choices.map((choice) => (
                  <button type="button" key={choice.id} className="kstw-choice"
                    onClick={() => { setPicker(''); act(() => axios.put(
                      `${API}/site-tags/${tagId}/project`, { project_id: choice.id })); }}
                    data-testid={`site-tag-project-${choice.id}`}>
                    <span className="kstw-choice-n">{choice.name}</span>
                    {choice.why ? <span className="kst-k">{choice.why}</span> : null}
                  </button>
                ))}
                <button type="button" className="kstw-choice"
                  onClick={() => { setPicker(''); act(() => axios.put(
                    `${API}/site-tags/${tagId}/project`, { project_id: null })); }}
                  data-testid="site-tag-project-none">
                  <span className="kstw-choice-n">No project — keep it as a firm tag</span>
                </button>
              </>
            ) : (picker === 'room' ? (tag.rooms || []) : (tag.trades || [])).map((row) => {
              const id = picker === 'room' ? row.id : row;
              const label = picker === 'room' ? row.name : row;
              return (
                <button type="button" key={id} className="kstw-choice"
                  onClick={() => {
                    setPicker('');
                    patch(picker === 'room' ? { room_id: id } : { trade: label });
                  }}
                  data-testid={`site-tag-pick-${id}`}>
                  <span className="kstw-choice-n">{label}</span>
                </button>
              );
            })}
          </div>
          <button type="button" className="kst-btn" onClick={() => setPicker('')}
            data-testid="site-tag-picker-close">Never mind</button>
        </div>
      ) : null}

      {telling ? (
        <TellSomeoneSheet about={{ type: 'site_tag', id: tagId }}
          onClose={() => setTelling(false)} />
      ) : null}

      {askingDesigner ? (
        <AskDesignerSheet
          projectId={tag.project_id}
          title={tag.title || `Tag ${tag.tag_number}`}
          url={`/site-tags/${tagId}/ask`}
          onAsked={() => load()}
          onClose={() => setAskingDesigner(false)}
        />
      ) : null}

      {big ? (
        <button type="button" className="kstp-big" onClick={() => setBig(null)}
          data-testid="site-tag-lightbox">
          <img src={big} alt="" />
        </button>
      ) : null}
    </div>
  );
}
